import { Res, InvoiceParamsFromPosDTO, SaleDTO, AddressDTO, AddressLocationDTO } from 'core/api';
import { CustomError, ServerError, handleServerErrors, Result, Events } from 'core/infrastructure';
import { TakeOutOrderFacade } from 'pos/facades';


export async function accept(orderId: number): Promise<void> {
	await run(async () => TakeOutOrderFacade.accept(orderId));
}

export async function reject(orderId: number): Promise<void> {
	await run(async () => TakeOutOrderFacade.reject(orderId));
}

export async function markAsPrepared(orderId: number): Promise<void> {
	await run(async () => TakeOutOrderFacade.markAsPrepared(orderId));
}

export async function deliver(orderId: number): Promise<void> {
	await run(async () => TakeOutOrderFacade.deliver(orderId));
}

export async function getDeliveryLocation(orderId: number): Promise<{ deliveryAddress: AddressDTO; location: AddressLocationDTO; }> {
	return await TakeOutOrderFacade.getDeliveryLocation(orderId);
}

export async function updateDeliveryAddress(orderId: number, updatedAddress: AddressDTO, location: AddressLocationDTO): Promise<Result> {
	return await run(async () => TakeOutOrderFacade.updateDeliveryAddress(orderId, updatedAddress, location));
}


export async function changeDeliveryPerson(orderId: number, deliveryPersonId: number): Promise<Result> {
	return await run(async () => await TakeOutOrderFacade.changeDeliveryPerson(orderId, deliveryPersonId));
}

export async function markAsDelivered(orderId: number): Promise<void> {
	await run(async () => TakeOutOrderFacade.markAsDelivered(orderId));
}

export async function cancel(orderId: number): Promise<Result> {

	const result = await run(async () => await TakeOutOrderFacade.cancel(orderId));
	if (!result.ok)
		return Result.error();

	const { success, errorMessage } = result.value;
	if (!success) {
		await Events.raise('pos:takeout-order:operation-failed', { msg: errorMessage });
		return Result.error();
	}

	return Result.ok();
}

export async function close(orderId: number, params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {
	return await run(async () => TakeOutOrderFacade.close(orderId, params));
}

async function run<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {
	let handledError: CustomError = new ServerError('ERROR NO CAPTURADO');

	const result = await handleServerErrors(fn, {
		onDomainError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: error.message });
			handledError = error;
		},
		onTicketNotFoundError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER() });
			handledError = error;
		},
		onFrozenSalesOrderError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: Res.Msg.MSG_SALES_ORDER_HAS_BEEN_CLOSED_BY_ANOTHER_USER() });
			handledError = error;
		},
		onFrozenPaymentsError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: Res.Msg.MSG_SALES_ORDER_HAS_BEEN_CLOSED_BY_ANOTHER_USER() });
			handledError = error;
		},
		onInvalidPaymentError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: error.message });
			handledError = error;
		},
		onPrintError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message) });
			handledError = error;
		},
		onInvalidCustomerCifError: async error => {
			await Events.raise('pos:takeout-order:operation-failed', { msg: Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE() });
			handledError = error;
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}